/**
 * The Finance BOARD (Tide phase 2, 2026-10-04): the Monday Finance board as
 * Finance's default view, built on the board kit (components/board;
 * docs/design/boards.md).
 *
 * One row per Finance board account. Tabs are the server's catalog: Monday's
 * groups when the Monday mirror carries them, the board's Status words until
 * then. Opening a row shows the resident's Finance dossier underneath it -
 * the same dossier, notes, expedited request and send-back as everywhere
 * else in Finance. Money is Monday-authored and read-only until the
 * authorship ruling, and every money cell says so. Approval, NOCA and FLMMIS
 * appear only when the server sent them (the caller may read approval facts).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BoardTabs, BoardTable, BoardLegend, TabsBasis, ColumnKey, Pager, StatusBar, NotRecorded, SourceTag,
  useBoardUrlState, boardDate, boardStamp, boardMoney, tabSourceText, panelId, tabId,
} from '../../components/board';
import type { BoardColumn, BoardTabDef } from '../../components/board';
import { StateBlock } from '../../components/tide';
import { outcomeOf, FinanceScope } from './financeApi';
import { financeBoardApi, FinanceBoardPage, FinanceBoardRow, FinanceFilterOption, MondayMoney } from './boardApi';
import ResidentDossier from './ResidentDossier';
import { APPROVAL_WORD_LABEL, approvalWordOf } from '../umed/umedApi';

const ID_BASE = 'fn-board';

function Money({ m, help }: { m: MondayMoney; help?: string }) {
  const text = boardMoney(m.value);
  if (text === null) return <NotRecorded why={help || 'The board holds no amount here.'} />;
  return <><span className="board-money">{text}</span><SourceTag source="MONDAY" detail="read-only" /></>;
}

const dated = (d: { on: string | null } | null) => (d && d.on ? <>{boardDate(d.on)}<SourceTag source="MONDAY" /></> : <NotRecorded />);

const HANDOFF_WORDS: Record<string, string> = {
  RECEIVED: 'received', ACCEPTED: 'accepted', INVOICED: 'invoiced', TERMS_RECORDED: 'terms recorded', COLLECTING: 'collecting',
  PAID_IN_FULL: 'paid in full', CLOSED: 'closed', RETURNED_TO_UMED: 'returned to UMED',
};

function columnsFor(page: FinanceBoardPage | null): BoardColumn<FinanceBoardRow>[] {
  const cols: BoardColumn<FinanceBoardRow>[] = [
    {
      key: 'status', label: 'Status', sort: 'status', source: 'MONDAY',
      help: 'The board\'s Status word, in the board\'s colour (a bar beside the word).',
      render: (r) => <><StatusBar word={r.status?.word} color={r.status?.color ?? null} /><SourceTag source="MONDAY" /></>,
    },
  ];
  if (page?.mode === 'GROUP') {
    cols.push({
      key: 'group', label: 'Monday group', source: 'MONDAY',
      help: `The account's group on the Monday board, read from the Monday mirror${page.groups.as_of ? ` as of ${boardStamp(page.groups.as_of)}` : ''}.`,
      render: (r) => (r.group ? <>{r.group.title || r.group.id}<SourceTag source="MONDAY" detail="mirror" /></> : <NotRecorded label="not in a group (yet)" />),
    });
  }
  cols.push(
    {
      key: 'rep', label: 'Rep', sort: 'rep', source: 'MONDAY',
      help: 'The board\'s Finance Rep (names from Monday user records).',
      render: (r) => (r.reps.length ? <>{r.reps.map((x) => x.name || 'Name not recorded').join(', ')}<SourceTag source="MONDAY" /></> : <NotRecorded label="no rep on the board" />),
    },
    {
      key: 'facility', label: 'Facility', sort: 'facility', source: 'MIXED',
      help: 'The building the board item links to ("board"); when it links none, the resident\'s current building in eMerge ("eMerge").',
      render: (r) => (r.facility ? <><span className="board-clip" title={r.facility.name || undefined}>{r.facility.name || 'Name not recorded'}</span><SourceTag source={r.facility.source} detail={r.facility.source === 'EMERGE' ? 'current building' : undefined} /></> : <NotRecorded />),
    },
    { key: 'sale', label: 'Sale value', sort: 'sale', numeric: true, source: 'MONDAY', help: 'The board\'s Total Sale Value. Monday-authored and read-only until the authorship ruling.', render: (r) => <Money m={r.sale_value} /> },
    { key: 'payments', label: 'Payments received', sort: 'payments', numeric: true, source: 'MONDAY', help: 'The board\'s Payments Received. Monday-authored, read-only. $0.00 is the board saying zero; "not recorded" is the board saying nothing.', render: (r) => <Money m={r.payments_received} /> },
    { key: 'remaining', label: 'Remaining', sort: 'remaining', numeric: true, source: 'MONDAY', help: 'The board\'s Remaining Amount. Monday-authored, read-only.', render: (r) => <Money m={r.remaining} /> },
    { key: 'overdue', label: 'Overdue', sort: 'overdue', numeric: true, source: 'MONDAY', help: 'The board\'s Amount Overdue. Monday-authored, read-only; eMerge does not recompute it.', render: (r) => <Money m={r.overdue} /> },
    {
      key: 'terms', label: 'Terms', source: 'MONDAY',
      help: 'The board\'s Terms text, quoted as the board wrote it and never parsed into numbers here.',
      render: (r) => (r.terms ? <><span className="board-clip" title={r.terms.text}>{r.terms.text}</span><SourceTag source="MONDAY" detail="Monday text" /></> : <NotRecorded />),
    },
  );
  if (page?.approval_detail) {
    cols.push(
      {
        key: 'approval', label: 'Approval', source: 'EMERGE',
        // Approved is the act (final verification 2026-10-06, P2-C): a UMED operator moved the case to Approved in eMerge.
        help: 'From the resident\'s UMED case in eMerge: "Approved" only when a UMED operator moved it to Approved in eMerge. FLMMIS reflected is the evidence that act needs, not the approval; Monday\'s approved stage is Monday\'s claim.',
        render: (r) => {
          if (!r.approval) return <NotRecorded label="no UMED case" />;
          const word = approvalWordOf(r.approval);
          return (
            <>
              <span className={word === 'APPROVED' ? 'board-tag board-tag--ok' : word === 'APPROVED_AT_SOURCE' ? 'board-tag board-tag--claim' : 'board-tag'}>
                {word === 'APPROVED' ? 'Approved' : APPROVAL_WORD_LABEL[word]}
              </span>
              {word === 'APPROVED' && r.approval.approved_on ? <span className="board-src">{boardDate(r.approval.approved_on)}</span> : null}
              <SourceTag source="EMERGE" detail="UMED case" />
            </>
          );
        },
      },
      { key: 'noca', label: 'NOCA', source: 'EMERGE', help: 'NOCA state on the resident\'s UMED case in eMerge.', render: (r) => (r.approval?.noca ? <>{r.approval.noca.toLowerCase().replace(/_/g, ' ')}<SourceTag source="EMERGE" /></> : <NotRecorded />) },
      { key: 'flmmis', label: 'FLMMIS', source: 'EMERGE', help: 'FLMMIS state on the resident\'s UMED case in eMerge.', render: (r) => (r.approval?.flmmis ? <>{r.approval.flmmis.toLowerCase().replace(/_/g, ' ')}<SourceTag source="EMERGE" /></> : <NotRecorded />) },
    );
  }
  cols.push(
    { key: 'reversal', label: 'Reversal date', sort: 'reversal', source: 'MONDAY', help: 'The board\'s Reversal Date.', render: (r) => dated(r.reversal_date) },
    {
      key: 'refund', label: 'Refund', sort: 'refund', source: 'MONDAY', help: 'The board\'s Refund Amount and Refund Date.',
      render: (r) => (r.refund ? <>{boardMoney(r.refund.amount) || <NotRecorded />}{r.refund.on ? <span className="board-src">{boardDate(r.refund.on)}</span> : null}<SourceTag source="MONDAY" /></> : <NotRecorded />),
    },
    { key: 'dcf', label: 'DCF correction', sort: 'dcf-correction', source: 'MONDAY', help: 'The board\'s Date of DCF Correction.', render: (r) => dated(r.dcf_correction_date) },
    { key: 'deceased', label: 'Deceased date', sort: 'deceased', source: 'MONDAY', help: 'The board\'s Deceased Date.', render: (r) => dated(r.deceased_date) },
    { key: 'invoice', label: 'Invoice created', source: 'MONDAY', help: 'The board\'s INVOICE CREATED.', render: (r) => (r.invoice_created ? <>{r.invoice_created.value ? 'Yes' : 'No'}<SourceTag source="MONDAY" /></> : <NotRecorded />) },
    { key: 'notes', label: 'Notes', numeric: true, source: 'EMERGE', help: 'Finance notes recorded in eMerge for this resident.', render: (r) => (r.notes_count === null ? <NotRecorded label="no resident" /> : <>{r.notes_count}<SourceTag source="EMERGE" /></>) },
    {
      key: 'handoff', label: 'Handoff', source: 'EMERGE',
      help: 'Finance\'s handoff state in eMerge, or that UMED has handed this resident to Finance and the handoff is still open. "Corrected <date>": a correction or reversal completed after the handoff, recorded on it (never a second handoff).',
      render: (r) => {
        if (!r.handoff) return <NotRecorded label="no resident" />;
        // Ruling #1 (2026-10-07): a correction after the handoff is an amendment on it -- said on the row, never a second handoff.
        const c = r.handoff.corrected;
        const corrected = c && c.count > 0
          ? <span className="board-tag board-tag--caution" title={`${c.count} amendment${c.count === 1 ? '' : 's'} on the handoff`}>Corrected {boardDate(c.last_on)}</span>
          : null;
        const pp = r.handoff.kind === 'PRIVATE_PAY' ? <span className="board-tag board-tag--pp">Private pay (no UMED)</span> : null;
        if (r.handoff.finance_state) return <>{pp}{HANDOFF_WORDS[r.handoff.finance_state] || r.handoff.finance_state.toLowerCase()}{corrected}<SourceTag source="EMERGE" /></>;
        if (r.handoff.umed_obligation_open) return <><span className="board-tag">handed by UMED, open</span>{corrected}<SourceTag source="EMERGE" /></>;
        return corrected ? <>{corrected}<SourceTag source="EMERGE" /></> : <span className="board-na">none open</span>;
      },
    },
  );
  return cols;
}

function FilterSelect({ label, value, options, onChange, allLabel }: { label: string; value: string; options: FinanceFilterOption[]; onChange: (v: string) => void; allLabel: string }) {
  return (
    <label className="board-field">
      <span>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} aria-label={label}>
        <option value="">{allLabel}</option>
        {value && !options.some((o) => o.id === value) && <option value={value}>Selected (not in this scope)</option>}
        {options.map((o) => <option key={o.id} value={o.id}>{o.name} ({o.count.toLocaleString('en-US')})</option>)}
      </select>
    </label>
  );
}

interface LoadError { message: string; status?: number; code?: string }

export default function FinanceBoard({ scope }: { scope: FinanceScope }) {
  const [st, update] = useBoardUrlState(['rep', 'building']);
  const [data, setData] = useState<FinanceBoardPage | null>(null);
  const [lastTabs, setLastTabs] = useState<BoardTabDef[]>([]);
  const [lastOptions, setLastOptions] = useState<FinanceBoardPage['filter_options'] | null>(null);
  const [err, setErr] = useState<LoadError | null>(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(st.q);
  const seq = useRef(0);
  useEffect(() => { setDraft(st.q); }, [st.q]);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true); setErr(null);
    try {
      const r = await financeBoardApi.board({ tab: st.tab, q: st.q, rep: st.filters.rep, building: st.filters.building, sort: st.sort, dir: st.dir, page: st.page });
      if (mine !== seq.current) return;
      if (r.success && r.data) {
        setData(r.data); setLastTabs(r.data.tabs); setLastOptions(r.data.filter_options);
      } else {
        // Never an empty board, never $0, on a failure: the rows are dropped and the failure is said.
        setData(null);
        setErr({ message: r.code === 'TAB_UNKNOWN' ? 'That view does not exist on this board right now. The tabs may have switched between Status words and Monday groups.' : outcomeOf(r), status: r.status, code: r.code });
      }
    } catch {
      if (mine === seq.current) { setData(null); setErr({ message: 'Could not reach the server, so nothing here is current.' }); }
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [st.tab, st.q, st.filters.rep, st.filters.building, st.sort, st.dir, st.page]);
  useEffect(() => { load(); }, [load]);

  const columns = useMemo(() => columnsFor(data), [data]);
  const tabs = data ? data.tabs : lastTabs.map((t) => ({ ...t, count: undefined }));
  const activeTab = tabs.find((t) => t.key === st.tab) || null;
  const options = data ? data.filter_options : lastOptions;
  const filtered = Boolean(st.q || st.filters.rep || st.filters.building);
  const clear = () => { setDraft(''); update({ q: '', filters: { rep: '', building: '' } }); };

  const identity = {
    label: 'Resident', sort: 'resident',
    help: 'The resident the board account belongs to. Open a row for their Finance dossier underneath: balance, who pays, approval, documents, history, notes, expedited dispense and send-back.',
    render: (r: FinanceBoardRow) => (
      <>
        <strong>{r.resident ? r.resident.name : 'No resident linked'}</strong>
        {r.resident_accounts !== null && r.resident_accounts > 1 && <span className="board-src">{r.resident_accounts} accounts on the board</span>}
      </>
    ),
    name: (r: FinanceBoardRow) => (r.resident ? r.resident.name : 'this account'),
    openable: (r: FinanceBoardRow) => Boolean(r.resident),
    closedWhy: () => 'The board item names no resident, so there is no dossier to open.',
  };

  return (
    <section className="board board-frame" aria-label="Finance board">
      {tabs.length > 0 && <BoardTabs tabs={tabs} value={st.tab} onChange={(k) => update({ tab: k, sort: null })} label="Finance board views" idBase={ID_BASE} />}
      <div role="tabpanel" id={panelId(ID_BASE)} aria-labelledby={activeTab ? tabId(ID_BASE, activeTab.key) : undefined} className="board-panel">
        <div className="board-head">
          {activeTab && (
            <p className="board-what">
              {(data && data.what) || activeTab.what}
              <span className="board-what__src">{tabSourceText(activeTab)}</span>
            </p>
          )}
          {data && <TabsBasis page={data} />}
          <div className="board-controls">
            <form className="board-search" role="search" onSubmit={(e) => { e.preventDefault(); update({ q: draft.trim() }); }}>
              <label className="board-field"><span>Resident or building</span>
                <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Name or building" aria-label="Search the Finance board" />
              </label>
              <button type="submit" className="board-btn">Find</button>
            </form>
            <FilterSelect label="Rep" value={st.filters.rep} options={options?.reps || []} allLabel="Every rep" onChange={(v) => update({ filters: { rep: v } })} />
            <FilterSelect label="Building" value={st.filters.building} options={options?.buildings || []} allLabel="Every building" onChange={(v) => update({ filters: { building: v } })} />
            {filtered && <button type="button" className="board-btn" onClick={clear}>Clear search and filters</button>}
            {data && (
              <span className="board-result" aria-live="polite">
                <strong>{data.total.toLocaleString('en-US')}</strong> {data.total === 1 ? 'account' : 'accounts'} in {activeTab?.label || 'this view'}{filtered ? ' matching your search and filters' : ''}{loading ? ' · updating…' : ''}
              </span>
            )}
          </div>
          {data && <BoardLegend title="Colour key" tabs={data.tabs} />}
        </div>

        {!data && loading && !err && <div className="board-state"><StateBlock kind="loading" shape="table" label="Loading the Finance board…" /></div>}
        {err && (
          <div className="board-state">
            <StateBlock kind="error" status={err.status} message={err.message} onRetry={load} />
            {err.code === 'TAB_UNKNOWN' && <p style={{ marginTop: 8 }}><button type="button" className="board-btn" onClick={() => update({ tab: 'all', sort: null })}>Open the main table</button></p>}
          </div>
        )}
        {data && data.items.length === 0 && data.total > 0 && (
          <div className="board-state">
            <StateBlock kind="empty" reason="filtered" message={`Page ${st.page} is past the end: this view has ${data.total.toLocaleString('en-US')} ${data.total === 1 ? 'account' : 'accounts'}.`} onClear={() => update({ page: 1 })} clearLabel="Go to page 1" />
          </div>
        )}
        {data && data.items.length === 0 && data.total === 0 && (
          <div className="board-state">
            {filtered
              ? <StateBlock kind="empty" reason="filtered" message={`No accounts in ${activeTab?.label || 'this view'} match your search and filters.`} onClear={clear} clearLabel="Clear search and filters" />
              : <StateBlock kind="empty" reason="none" message={`No account is in ${activeTab?.label || 'this view'} right now.`} />}
          </div>
        )}
        {data && data.items.length > 0 && (
          <BoardTable<FinanceBoardRow>
            label={`Finance board — ${activeTab?.label || data.tab}, ${data.total} ${data.total === 1 ? 'account' : 'accounts'}`}
            columns={columns}
            rows={data.items}
            rowKey={(r) => r.id}
            identity={identity}
            openKey={st.open}
            onToggle={(k) => update({ open: st.open === k ? null : k })}
            detailLabel={(r) => `${r.resident ? r.resident.name : 'Account'}: Finance dossier`}
            renderDetail={(r) => (r.resident ? (
              <>
                {r.resident_accounts !== null && r.resident_accounts > 1 && <p className="board-basis" style={{ padding: '8px 16px 0' }}>This resident has {r.resident_accounts} accounts on the board; the dossier is the resident&rsquo;s, the same for each.</p>}
                <ResidentDossier patientId={r.resident.id} scope={scope} />
              </>
            ) : null)}
            sort={data.sort} dir={data.dir} sorts={data.sorts}
            onSort={(col, dir) => update({ sort: col, dir })}
            busy={loading}
          />
        )}
        {data && <Pager page={data.page} limit={data.limit} total={data.total} onPage={(p) => update({ page: p })} label="accounts" />}
      </div>
      <div className="board-foot">
        {data && activeTab && (
          <p>
            Counted and listed by one predicate ({activeTab.predicate}); the count on the tab is the number of accounts here.
            {' '}Board mirrored through {boardStamp(data.synced_at) || 'an unrecorded time'}. &ldquo;not recorded&rdquo; means the board holds no value, never $0 and never a failed column.
          </p>
        )}
        <p>Money is Monday-authored and read-only in eMerge until the authorship ruling: the board&rsquo;s sync overwrites it twice a day.</p>
        <ColumnKey columns={columns} identity={identity} />
      </div>
    </section>
  );
}
